import { createPortal } from 'react-dom';
import { ReactNode, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { api } from '../../services/api';
import { Invitee, TRAVEL_LABEL } from '../../lib/events';
import { personName } from '../../lib/programs';
import { fmt } from '../../lib/format';
import { SkeletonRows, ErrorNotice } from '../ui/States';
import { headRow, th, td, em } from './logistics';

export function useInvitees(eventId: string, enabled = true) {
  return useQuery<{ invitees: Invitee[]; needsVisible: boolean }>(
    ['event-invitees', eventId, '', '', false],
    async () => (await api.get(`/events/${eventId}/invitees`)).data.data,
    { enabled, staleTime: 30000 },
  );
}

const needsOf = (i: Invitee, show: 'needs' | 'dietary') =>
  show === 'dietary'
    ? (i.needs?.dietary ?? '')
    : [
        i.needs?.attendanceMode === 'virtual' ? 'online' : null,
        i.needs?.travelMode ? (TRAVEL_LABEL[i.needs.travelMode] ?? i.needs.travelMode) : null,
        i.needs?.hotel ? 'hotel' : null,
        i.needs?.dietary ? `diet: ${i.needs.dietary}` : null,
      ]
        .filter(Boolean)
        .join(' · ');

/** Names behind a number (admins only: the dashboard sends ids to admins). */
export default function PeopleList({
  eventId,
  ids,
  show = 'needs',
}: {
  eventId: string;
  ids: string[];
  show?: 'needs' | 'dietary';
}) {
  const list = useInvitees(eventId);
  if (list.isLoading) return <SkeletonRows rows={4} />;
  if (list.error) return <ErrorNotice>Could not load the people.</ErrorNotice>;
  const wanted = new Set(ids);
  const rows = (list.data?.invitees ?? [])
    .filter((i) => wanted.has(i.id))
    .sort((a, b) => personName(a.person).localeCompare(personName(b.person)));
  if (!rows.length) return <p className="text-[15px] leading-[22px] text-ink-2">Nobody.</p>;
  return (
    <table className="w-full tabular max-md:table">
      <thead>
        <tr className={headRow}>
          <th className={th}>Name</th>
          <th className={th}>Affiliation</th>
          <th className={th}>{show === 'dietary' ? 'Dietary needs' : 'Needs'}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((i) => {
          const needs = needsOf(i, show);
          return (
            <tr key={i.id} className="border-b border-line">
              <td className={td}>
                {personName(i.person)}
                {i.person.email ? <span className={em}>{i.person.email}</span> : null}
              </td>
              <td className={`${td} text-ink-2`}>{i.affiliation ?? <span className="text-ink-3">–</span>}</td>
              <td className={td}>{needs || <span className="text-ink-3">–</span>}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/**
 * The people behind a number: a side sheet over the page (a full-width sheet on a phone), titled
 * "{label} · {n} people", with the standard table and "Open in People".
 */
export function PeopleSheet({
  eventId,
  label,
  ids,
  count,
  show = 'needs',
  onClose,
  onOpenPeople,
  children,
}: {
  eventId: string;
  label: string;
  ids: string[];
  count?: number;
  show?: 'needs' | 'dietary';
  onClose: () => void;
  onOpenPeople?: () => void;
  children?: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    box.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (opener && document.contains(opener)) opener.focus();
    };
  }, []);
  const n = count ?? ids.length;
  const title = `${label} · ${fmt(n)} ${n === 1 ? 'person' : 'people'}`;
  return createPortal(
    <div
      className="fixed inset-0 z-[60] motion-safe:animate-fade-in"
      style={{ background: 'rgba(42,36,32,.25)' }}
      onClick={onClose}
    >
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="absolute right-0 top-0 h-full w-[560px] max-w-full overflow-y-auto overscroll-contain bg-surface shadow-lift border-l border-line p-6 text-[15px] leading-[22px] text-ink-2 outline-none motion-safe:animate-panel-in max-md:inset-3 max-md:top-[max(12px,env(safe-area-inset-top))] max-md:h-auto max-md:w-auto max-md:rounded-md max-md:border max-md:p-4 max-md:motion-safe:animate-sheet-in"
      >
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
          <h2 className="font-serif font-semibold text-[22px] leading-[30px] text-ink">
            {label}{' '}
            <span className="font-sans font-normal text-[15px] text-ink-3">
              · {fmt(n)} {n === 1 ? 'person' : 'people'}
            </span>
          </h2>
          <div className="flex items-center gap-4">
            {onOpenPeople ? (
              <button
                type="button"
                className="text-accent underline underline-offset-[3px] hover:text-ink min-h-[44px]"
                onClick={onOpenPeople}
              >
                Open in People
              </button>
            ) : null}
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-sm border border-line-strong text-ink hover:bg-selected"
            >
              <svg
                aria-hidden="true"
                width="14"
                height="14"
                viewBox="0 0 14 14"
                stroke="currentColor"
                strokeWidth="1.75"
              >
                <path d="M2 2l10 10M12 2L2 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="mt-3">
          <PeopleList eventId={eventId} ids={ids} show={show} />
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
